import { useState } from "react";
import { useIntl } from "react-intl";

import { AuthService } from "../../../../api/auth.service";
import { LicenseService } from "../../../../api/license.service";
import { authStore } from "../../../../store/auth.store";
import { trackEvent } from "../../../../utils/analytics";
import { Card, CardHeader, CardTitle, CardContent } from "../../../ui/card";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Alert } from "../../../ui/alert";

// what the server says in the body of a 422 when the code was redeemed before; any other
// 422 means the code exists but is for a product that cannot be activated this way
const ALREADY_ACTIVATED_DETAIL = 'License already activated';

const errorMessageId = (status?: number, detail?: string) => {
  switch (status) {
    case 404:
      return 'LICENSE_NOT_FOUND';
    case 422:
      return detail === ALREADY_ACTIVATED_DETAIL ? 'LICENSE_ALREADY_ACTIVATED' : 'LICENSE_NOT_ACTIVATABLE';
    case 429:
      return 'LICENSE_TOO_MANY_ATTEMPTS';
    default:
      return 'LICENSE_ACTIVATION_ERROR';
  }
};

// expiration is a plain date that ends at the start of that day in UTC
const isInFuture = (date?: string | null): date is string =>
  !!date && date > new Date().toISOString().slice(0, 10);

const Activation = () => {
  const { formatMessage, formatDate } = useIntl();

  const expires = authStore(state => state.user?.expires);

  const [code, setCode] = useState("");
  const [activating, setActivating] = useState(false);
  const [activated, setActivated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedCode = code.trim();

  // the store's own getCurrentUser swaps the whole app for the loading page while it runs,
  // which would drop this section and the result it is about to show
  const refreshPremium = async () => {
    try {
      const { data } = await AuthService.getCurrentUser();
      authStore.setState(state => ({
        user: state.user && { ...state.user, plan: data.plan, expires: data.expires },
      }));
    } catch {
      // the license is activated either way; the date catches up on the next load
    }
  };

  const activate = async () => {
    if (!trimmedCode || activating) return;
    setActivating(true);
    setActivated(false);
    setError(null);
    try {
      await LicenseService.activate(trimmedCode);
      trackEvent('license_activate');
      await refreshPremium();
      setActivated(true);
      setCode("");
    } catch (e: any) {
      setError(formatMessage({ id: errorMessageId(e?.response?.status, e?.response?.data?.detail) }));
    } finally {
      setActivating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{formatMessage({ id: 'LICENSE_ACTIVATION' })}</CardTitle>
        <p className="text-sm text-muted-foreground">
          {formatMessage({ id: 'LICENSE_ACTIVATION_DESCRIPTION' })}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && <Alert variant="error" message={error} />}
        {activated && <Alert variant="success" message={formatMessage({ id: 'LICENSE_ACTIVATED' })} />}

        {isInFuture(expires) && (
          <p className="text-sm">
            {formatMessage(
              { id: 'LICENSE_PREMIUM_UNTIL' },
              { date: formatDate(expires, { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }) }
            )}
          </p>
        )}

        <div className="space-y-2">
          <label className="text-sm font-medium" htmlFor="license-code">
            {formatMessage({ id: 'LICENSE_CODE' })}
          </label>
          <div className="flex max-w-md gap-2">
            <Input
              id="license-code"
              value={code}
              autoComplete="off"
              spellCheck={false}
              disabled={activating}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && activate()}
            />
            <Button
              variant="primary"
              disabled={!trimmedCode}
              loading={activating}
              onClick={activate}
            >
              {formatMessage({ id: 'ACTIVATE' })}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default Activation;
